import { ref, onMounted, onUnmounted, nextTick } from "vue"
import { useConversationStore } from "@/store/conversation"
import { useConversationService } from "@/services/conversationService"
import type { ConversationItem } from "@/types/app"
import { FileIcon, ImageIcon, PdfIcon, ExeclIcon } from "@/components/icons"

// 响应式移动端判断（基于窗口宽度）
export function useResponsiveMobileView(breakpoint = 768) {
  const isMobileView = ref(window.innerWidth <= breakpoint)
  function handleResize() {
    isMobileView.value = window.innerWidth <= breakpoint
  }
  onMounted(() => {
    window.addEventListener("resize", handleResize)
  })
  onUnmounted(() => {
    window.removeEventListener("resize", handleResize)
  })
  return isMobileView
}

// 设备级移动端判断（基于UA/触摸）
export function isMobileDevice() {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0
  return isMobile || hasTouch
}

/**
 * 检查对象是否为空
 * @param obj 要检查的对象
 * @returns 是否为空
 */
export function isEmpty(obj: any): boolean {
  if (obj === null || obj === undefined) return true
  if (typeof obj === "string") return obj.trim().length === 0
  if (Array.isArray(obj)) return obj.length === 0
  if (typeof obj === "object") return Object.keys(obj).length === 0
  return false
}

/**
 * 获取文件类型和大小信息，返回格式如 "PDF 1.65MB" 或 "JPG 155KB"
 * @param file 文件对象，包含 file_name 和 file_size 属性
 * @returns 格式化的文件类型和大小字符串
 */
export function getFileType(file: { file_name: string; file_size: number }): string {
  const extension = file.file_name.split(".").pop()?.toLowerCase()
  let size: string
  if (file.file_size < 1024) {
    size = file.file_size + "B"
  } else if (file.file_size < 1024 * 1024) {
    size = (file.file_size / 1024).toFixed(0) + "KB"
  } else {
    size = (file.file_size / (1024 * 1024)).toFixed(1) + "MB"
  }
  return `${extension?.toUpperCase() || "FILE"} ${size}`
}

/**
 * 根据文件扩展名获取对应的图标组件名称
 * @param fileName 文件名
 * @returns 图标组件名称
 */
export function getFileIconName(fileName: string): string {
  if (!fileName) return "FileIcon"
  const extension = fileName.split(".").pop()?.toLowerCase()

  // 图片类型
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "ico"].includes(extension || "")) {
    return "ImageIcon"
  }
  // PDF类型
  if (extension === "pdf") {
    return "PdfIcon"
  }
  // Excel类型
  if (["xls", "xlsx", "csv"].includes(extension || "")) {
    return "ExeclIcon"
  }
  // 默认文件图标
  return "FileIcon"
}

/**
 * 根据文件名获取对应的图标组件
 * @param fileName 文件名
 * @returns 图标组件
 */
export function getFileIcon(fileName: string) {
  const iconName = getFileIconName(fileName)
  const iconMap = { FileIcon, ImageIcon, PdfIcon, ExeclIcon }
  return iconMap[iconName as keyof typeof iconMap] || FileIcon
}

/**
 * 下载或在新窗口打开文件 (跨域允许则触发下载, 否则降级为新窗口打开)
 * @param fileUrl 文件 URL
 * @param fileName 下载使用的文件名
 */
export async function openFileUrl(fileUrl: string, fileName?: string): Promise<void> {
  if (!fileUrl) return
  try {
    const response = await fetch(fileUrl, { mode: "cors" })
    if (!response.ok) throw new Error(`下载失败: ${response.status} ${response.statusText}`)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = blobUrl
    link.download = fileName || "下载文件"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error("下载失败:", error)
    window.open(fileUrl, "_blank")
  }
}

/**
 * 判断是否为数值类型
 * @param value 要判断的值
 * @returns 是否为数值类型
 */
export function isNumericValue(value: unknown): boolean {
  if (typeof value === "number") return true
  if (typeof value !== "string") return false

  if (value.includes("%")) {
    return !isNaN(parseFloat(value.replace("%", "")))
  }

  const numericPart = value.replace(/[^\d.-]/g, "")
  return !isNaN(parseFloat(numericPart))
}

/**
 * 解析数值，处理各种格式（包括百分比、带单位的数字等）
 * @param value 要解析的值
 * @returns 解析后的数值
 */
export function parseValue(value: unknown): number {
  if (typeof value === "number") return value
  if (typeof value !== "string") return 0

  if (value.includes("%")) {
    return parseFloat(value.replace("%", "")) || 0
  }

  const numericPart = value.replace(/[^\d.-]/g, "")
  return parseFloat(numericPart) || 0
}

/**
 * 智能格式化数字显示
 * @param value 要格式化的数值
 * @returns 格式化后的字符串
 */
export function smartFormatNumber(value: unknown): string {
  if (typeof value !== "number" || isNaN(value)) return "0"

  if (Number.isInteger(value)) {
    return value.toString()
  }

  if (value % 1 === 0) {
    return value.toString()
  }

  return value.toFixed(2)
}

/**
 * 解析字符串数组（逗号分隔）
 * @param value 逗号分隔的字符串
 * @returns 解析后的字符串数组
 */
export function parseStringArray(value: string | undefined): string[] {
  if (!value) return []
  return value
    .split(",")
    .map((f) => f.trim())
    .filter((f) => f)
}

/**
 * 历史会话弹框公共逻辑
 * @returns 历史会话弹框相关的状态和方法
 */
export function useHistoryDialog() {
  const conversationStore = useConversationStore()
  const conversationService = useConversationService()
  const showHistoryDialog = ref(false)

  // 处理显示历史对话对话框
  function handleShowHistoryDialog() {
    // 先重置状态
    showHistoryDialog.value = false
    // 使用 nextTick 确保状态已重置
    nextTick(() => {
      showHistoryDialog.value = true
    })
  }

  // 处理单个删除
  async function handleDeleteOne(conversationId: string) {
    try {
      // 如果删除的是当前会话，需要切换到新会话
      if (conversationId === conversationStore.currentConversationId) {
        conversationStore.createNewConversation()
      }

      // 刷新会话列表
      await conversationService.initialize()
    } catch (error) {
      console.error("处理单个删除失败:", error)
    }
  }

  // 处理批量删除
  async function handleDeleteMulti(rows: ConversationItem[]) {
    try {
      // 如果删除的会话中包含当前会话，需要切换到新会话
      const currentConversationId = conversationStore.currentConversationId
      const deletedCurrent = rows.some((row: ConversationItem) => row.conversation_id === currentConversationId)

      if (deletedCurrent) {
        // 删除当前会话后，创建新会话
        conversationStore.createNewConversation()
      }

      // 刷新会话列表
      await conversationService.initialize()
    } catch (error) {
      console.error("处理批量删除失败:", error)
    }
  }

  return {
    showHistoryDialog,
    handleShowHistoryDialog,
    handleDeleteOne,
    handleDeleteMulti
  }
}

/**
 * HTML 实体转义，防止 XSS 攻击
 * @param text 需要转义的文本
 * @returns 转义后的安全文本
 */
export function escapeHtml(text: string): string {
  const htmlEntities: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }
  return text.replace(/[&<>"']/g, (char) => htmlEntities[char] || char)
}

/**
 * 计算 Markdown 表格行的列数
 * @param line 表格行字符串
 * @returns 列数（非表格行返回 0）
 */
export function countTableColumns(line: string): number {
  const trimmed = line.trim()
  if (!trimmed.startsWith("|")) return 0
  const pipeCount = (trimmed.match(/\|/g) || []).length
  return trimmed.endsWith("|") ? pipeCount - 1 : pipeCount
}

/** 生成指定列数的 Markdown 表格分隔符行 */
export function generateSeparator(colCount: number): string {
  return "|" + Array(colCount).fill("---").join("|") + "|"
}

/**
 * 规范化 Markdown 表格分隔符行
 * 自动修复分隔符行列数与表头行不匹配的问题（兼容大模型返回的不规范表格）
 * @param content Markdown 内容
 * @returns 修复后的 Markdown 内容
 */
export function normalizeMarkdownTables(content: string): string {
  if (!content) return content

  const lines = content.split("\n")
  const result: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    // 判断是否为分隔符行：以 | 开头，去除 | 后只包含空白、- 和 :
    const isSeparator = trimmed.startsWith("|") && /^[\s|\-:]*$/.test(trimmed)

    if (isSeparator && i > 0) {
      // 向前找表头行（包含 | 的非空行）
      let headerIdx = i - 1
      while (headerIdx >= 0) {
        const h = lines[headerIdx].trim()
        if (h && h.includes("|")) break
        headerIdx--
      }

      if (headerIdx >= 0) {
        const headerCols = countTableColumns(lines[headerIdx])
        const sepCols = countTableColumns(line)

        if (headerCols > 0 && headerCols !== sepCols) {
          result.push(generateSeparator(headerCols))
          continue
        }
      }
    }

    result.push(line)
  }

  return result.join("\n")
}

/**
 * 判断是否为 Agentic 域名
 */
export const isAgenticDomain = () => {
  const hostname = window.location.hostname
  // 开发环境下可以通过 search 参数 platform=agentic 强制切换，方便调试
  if (window.location.href.includes("platform=agentic")) return true
  return hostname.includes("agentic")
}

/**
 * 智齿工作台 postMessage 来源校验：jlpay/zhichi/sobot 域（含子域）
 */
export function isAllowedSobotOrigin(origin: string): boolean {
  return /^(https?:\/\/)([a-zA-Z0-9-]+\.)*(jlpay\.com|zhichi\.com|sobot\.com)(:\d+)?$/i.test(origin)
}

/**
 * 字符串层面从 search 中剔除指定 key，返回剩余 query（含 ?，无剩余参数时返回 ""）
 * 登录跳转回传 URL 参数专用：不经 vue-router query 对象往返，避免特殊字符（如整段编码参数）被二次编码
 */
export function stripQueryKeys(search: string, keys: string[]): string {
  if (!search || search[0] !== "?") return ""
  const kept = search
    .slice(1)
    .split("&")
    .filter((pair) => {
      if (!pair) return false
      const key = pair.split("=")[0]
      return !keys.includes(key)
    })
  return kept.length > 0 ? `?${kept.join("&")}` : ""
}

/**
 * 构造 sobot-summary 跳转登录页的 URL（未登录守卫 / 登录失效 / 页面防御检查共用）：
 * /login?target=%2Fsobot-summary&<当前页剩余参数（剔除 target/code/state）>
 * 剩余参数必须以 "&" 续接：直接拼接 window.location.search 会引入第二个 "?"，
 * 使 target 值吞掉首个参数（如 target=/sobot-summary?cid=c1、cid=c1?uid=u1 互相污染）
 */
export function buildSobotLoginUrl(): string {
  const rest = stripQueryKeys(window.location.search, ["target", "code", "state"])
  return "/login?target=" + encodeURIComponent("/sobot-summary") + (rest ? "&" + rest.slice(1) : "")
}

const WECOM_OAUTH_DIRECT_LOGIN_DOMAINS = ["ai.jlpay.com", "ai-fat.jlpay.com", "agentic.jlpay.com"]

/**
 * 判断当前登录页是否无需企业微信 OAuth 中转
 */
export const isWecomDirectLoginDomain = (hostname = window.location.hostname) => {
  return WECOM_OAUTH_DIRECT_LOGIN_DOMAINS.includes(hostname.toLowerCase())
}

/**
 * 企业微信 OAuth 回跳目标白名单：*.jlpay.com
 */
export const isAllowedWecomOAuthTarget = (target: string) => {
  try {
    const targetUrl = new URL(target)
    const hostname = targetUrl.hostname.toLowerCase()
    const isHttpOrHttps = ["http:", "https:"].includes(targetUrl.protocol)
    if (!isHttpOrHttps) return false

    // 白名单域名
    if (hostname.endsWith(".jlpay.com")) return true

    // fat 环境下支持本地调试地址
    if (import.meta.env.VITE_APP_DIRNAME === "test") {
      return true
    }

    return false
  } catch (error) {
    return false
  }
}

/**
 * 根据环境选择企业微信 OAuth 中转域名
 */
export const getWecomOAuthTransferOrigin = () => {
  const fatTransferEnvList = ["dev", "serve", "test", "ywuat"]
  const hostname = fatTransferEnvList.includes(import.meta.env.VITE_APP_DIRNAME) ? "ai-fat.jlpay.com" : "ai.jlpay.com"
  return `https://${hostname}`
}

/**
 * 获取当前登录页作为 OAuth 回跳目标，并移除旧授权参数
 */
export const getCurrentWecomOAuthTarget = () => {
  const targetUrl = new URL(window.location.href)
  targetUrl.searchParams.delete("code")
  targetUrl.searchParams.delete("state")
  return targetUrl.toString()
}

/**
 * 构造企业微信 OAuth 中转页地址
 */
export const buildWecomOAuthTransferUrl = (target: string) => {
  const transferUrl = new URL("/login", getWecomOAuthTransferOrigin())
  transferUrl.searchParams.set("target", target)
  return transferUrl.toString()
}

/**
 * 构造带 code 的业务域名回跳地址，兼容 target 自带查询参数的场景
 */
export const buildWecomOAuthTargetUrl = (target: string, code: string) => {
  if (!isAllowedWecomOAuthTarget(target)) return ""

  const targetUrl = new URL(target)
  targetUrl.searchParams.set("code", code)
  return targetUrl.toString()
}
